import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, faqs } from '@/mocks/videos';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91在线视频',
    url: 'https://www.91onlinevideo.com',
    description: aboutInfo.introduction,
    email: aboutInfo.contact.email,
    telephone: aboutInfo.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    foundingDate: '2018',
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLdOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]} />

          <section className="max-w-4xl mx-auto py-10 md:py-14">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                关于91在线视频
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                {aboutInfo.slogan}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
              {aboutInfo.achievements.map((item) => (
                <div key={item.label} className="text-center p-4 rounded-xl bg-background-100">
                  <div className="text-xl md:text-2xl font-bold font-heading text-primary-600">
                    {item.number}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="prose prose-sm max-w-none mb-12">
              <h2 className="text-lg font-semibold text-foreground-800 mb-3">
                <a href="/about#introduction">品牌介绍</a>
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.introduction}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                <div className="p-4 rounded-lg bg-background-100">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">
                    <i className="ri-flag-line text-primary-500 mr-1.5"></i>
                    我们的使命
                  </h3>
                  <p className="text-xs text-foreground-600">{aboutInfo.mission}</p>
                </div>
                <div className="p-4 rounded-lg bg-background-100">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">
                    <i className="ri-eye-line text-accent-500 mr-1.5"></i>
                    我们的愿景
                  </h3>
                  <p className="text-xs text-foreground-600">{aboutInfo.vision}</p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-800 mb-4">
                <a href="/about#values">核心价值观</a>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutInfo.values.map((v) => (
                  <div key={v.title} className="p-4 rounded-lg border border-background-200 bg-background-50">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">
                      <i className="ri-check-double-line text-primary-500 mr-1.5"></i>
                      {v.title}
                    </h3>
                    <p className="text-xs text-foreground-600">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-800 mb-4">
                <a href="/about#contact">联系方式</a>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">电子邮箱</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">客服电话</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">公司地址</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">微信公众号</h3>
                    <p className="text-xs text-foreground-600 mt-0.5">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>
            </div>

            <div id="faq-section">
              <h2 className="text-lg font-semibold text-foreground-800 mb-4">
                <a href="/about#faq">常见问题</a>
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    id={`faq-${index}`}
                    className="rounded-lg border border-background-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-background-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <i className={`text-foreground-500 flex-shrink-0 transition-transform ${openFaq === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-4 pb-4">
                        <p className="text-xs text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}